'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Megaphone } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { FARM_EQUIPMENT_LABEL, GRAIN_FEED_LABEL } from '@/lib/features';
import { createClient } from '@/lib/supabase/client';
import { fetchUnreadCount } from '@/lib/messaging';

/**
 * Every sector on the site, in one list.
 *
 * These used to be eleven separate links across the top bar. Together with the
 * seven buttons on the right that overflowed the width — at any browser zoom
 * above 100% the whole page scrolled sideways. They now live in one "All
 * Sectors" panel, which shows the lot at once rather than hiding some of them,
 * with the four busiest also pinned as direct links.
 */
const sectors: { label: string; href: string; icon: string; blurb: string }[] = [
  { label: 'Livestock', href: '/marketplace-listings?cat=livestock', icon: 'TagIcon', blurb: 'Cattle, sheep, pigs, poultry and more' },
  { label: 'Machinery', href: '/marketplace-listings?cat=machinery', icon: 'WrenchScrewdriverIcon', blurb: 'Tractors, balers, trailers, attachments' },
  { label: 'Machinery Hire', href: '/machinery-hire', icon: 'ClockIcon', blurb: 'Kit and contractors available to hire' },
  { label: GRAIN_FEED_LABEL, href: '/crop-feed-bedding', icon: 'GlobeEuropeAfricaIcon', blurb: 'Grain, forage, straights, straw and bedding' },
  { label: FARM_EQUIPMENT_LABEL, href: '/marketplace-listings?cat=farm-equipment', icon: 'ArchiveBoxIcon', blurb: 'Deadstock, fencing, supplies and equipment' },
  { label: 'Wanted', href: '/buyer-requests', icon: 'MagnifyingGlassIcon', blurb: 'Buyers looking for stock and kit' },
  { label: 'Haulage Jobs', href: '/haulage-job-board', icon: 'TruckIcon', blurb: 'Loads needing moving, posted by farmers' },
  { label: 'Hauliers Directory', href: '/hauliers-directory', icon: 'BuildingOffice2Icon', blurb: 'Licensed livestock and machinery hauliers' },
  { label: 'Auctions', href: '/timed-auctions', icon: 'BellAlertIcon', blurb: 'Timed online sales with live bidding' },
  { label: 'News & Updates', href: '/news-updates', icon: 'NewspaperIcon', blurb: 'Market comment and platform news' },
  { label: 'Verified Members', href: '/verified', icon: 'CheckBadgeIcon', blurb: 'What the verified badge means' },
];

// Pinned alongside the All Sectors button so the busiest boards stay one click away.
const quickLinks = [
  { label: 'Livestock', href: '/marketplace-listings?cat=livestock' },
  { label: 'Machinery', href: '/marketplace-listings?cat=machinery' },
  { label: GRAIN_FEED_LABEL, href: '/crop-feed-bedding' },
  { label: 'Auctions', href: '/timed-auctions' },
];

export default function SiteHeader() {
  const supabase = useMemo(() => createClient(), []);
  const [unread, setUnread] = useState(0);

  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sectorsOpen, setSectorsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const sectorsRef = useRef<HTMLDivElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const { user, profile, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {}
  };

  // Unread badge on the Messages link. Refreshed on mount and every 60s —
  // often enough to feel live without hammering the database.
  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    let active = true;
    const load = async () => {
      const count = await fetchUnreadCount(supabase);
      if (active) setUnread(count);
    };
    load();
    const timer = setInterval(load, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [user, supabase]);

  // A dropdown left hanging open over the page is worse than no dropdown, so
  // close on an outside click, on Escape, and whenever the route changes.
  useEffect(() => {
    if (!sectorsOpen && !accountOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (sectorsOpen && sectorsRef.current && !sectorsRef.current.contains(target)) setSectorsOpen(false);
      if (accountOpen && accountRef.current && !accountRef.current.contains(target)) setAccountOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSectorsOpen(false);
        setAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [sectorsOpen, accountOpen]);

  useEffect(() => {
    setSectorsOpen(false);
    setAccountOpen(false);
    setMobileOpen(false);
  }, [pathname]);

  const isActive = (href: string) => {
    const path = href.split('?')[0];
    return pathname === path || (path !== '/' && pathname.startsWith(path));
  };

  return (
    <header className="bg-primary text-primary-foreground sticky top-0 z-50 shadow-md">
      {/* Ad banner leaderboard */}
      <div className="bg-muted border-b border-border hidden md:flex items-center justify-center py-1">
        <div className="ad-banner-placeholder w-full max-w-[728px] h-[50px] rounded text-xs">
          <Megaphone className="w-3.5 h-3.5 inline mr-1.5 align-text-bottom" aria-hidden="true" />ADVERTISE HERE — 728×90 Leaderboard Banner — Contact contact@farmtradedirect.com
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16 gap-5">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 shrink-0">
            <AppLogo size={36} />
            <div className="flex flex-col leading-tight">
              <span className="font-extrabold text-base tracking-tight text-primary-foreground">FarmTradeDirect</span>
              <span className="text-[10px] text-green-300 font-medium tracking-wide hidden xl:block">UK&apos;s Most Complete Agricultural Marketplace</span>
            </div>
          </Link>

          {/* Desktop nav — one panel with every sector, plus the busiest four */}
          <nav className="hidden lg:flex items-center gap-1 min-w-0">
            <div className="relative" ref={sectorsRef}>
              <button
                type="button"
                onClick={() => { setSectorsOpen((o) => !o); setAccountOpen(false); }}
                aria-expanded={sectorsOpen}
                aria-haspopup="true"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-bold transition-all duration-150 ${
                  sectorsOpen ? 'bg-white/20 text-white' : 'text-white hover:bg-white/10'
                }`}
              >
                <Icon name="Squares2X2Icon" size={16} />
                All Sectors
                <Icon name="ChevronDownIcon" size={14} className={sectorsOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>

              {sectorsOpen && (
                <div className="absolute left-0 top-full mt-2 w-[min(46rem,calc(100vw-3rem))] bg-card text-foreground rounded-xl shadow-2xl border border-border p-3 z-50">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-2 pb-2">
                    Everything Agriculture. One Marketplace.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-0.5">
                    {sectors.map((sector) => (
                      <Link
                        key={`sector-${sector.label}`}
                        href={sector.href}
                        onClick={() => setSectorsOpen(false)}
                        className="flex items-start gap-2.5 px-2.5 py-2 rounded-lg hover:bg-muted transition-colors group"
                      >
                        <Icon name={sector.icon} size={18} className="text-primary shrink-0 mt-0.5" />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                            {sector.label}
                          </span>
                          <span className="block text-xs text-muted-foreground leading-snug">{sector.blurb}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                  <div className="border-t border-border mt-2 pt-2 px-2 flex items-center justify-between gap-3">
                    <Link
                      href="/marketplace-listings"
                      onClick={() => setSectorsOpen(false)}
                      className="text-sm font-semibold text-primary hover:underline"
                    >
                      Browse every listing
                    </Link>
                    <Link
                      href="/post-listing"
                      onClick={() => setSectorsOpen(false)}
                      className="btn-accent text-xs py-1.5 px-3"
                    >
                      <Icon name="PlusCircleIcon" size={14} />
                      Post a Listing
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {quickLinks.map((item) => (
              <Link
                key={`quick-${item.label}`}
                href={item.href}
                className={`px-3 py-2 rounded-md text-sm font-600 whitespace-nowrap transition-all duration-150 ${
                  isActive(item.href) ? 'bg-white/20 text-white' : 'text-green-100 hover:bg-white/10 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right actions — Post Listing, Membership, then everything
              account-related folded into one menu. */}
          <div className="flex items-center gap-2 shrink-0">
            <Link
              href="/membership-pricing"
              className="hidden 2xl:flex items-center gap-1.5 text-sm font-semibold text-amber-300 hover:text-amber-200 transition-colors whitespace-nowrap"
            >
              <Icon name="StarIcon" size={15} variant="solid" />
              Membership
            </Link>
            <Link href="/post-listing" className="hidden md:flex btn-accent text-sm py-2 px-4 shrink-0 whitespace-nowrap">
              <Icon name="PlusCircleIcon" size={16} />
              Post Listing
            </Link>

            {user ? (
              <div className="relative" ref={accountRef}>
                <button
                  type="button"
                  onClick={() => { setAccountOpen((o) => !o); setSectorsOpen(false); }}
                  aria-expanded={accountOpen}
                  aria-haspopup="true"
                  className="relative flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-3 py-2 rounded-md transition-all duration-150"
                >
                  <Icon name="UserCircleIcon" size={16} />
                  <span className="hidden sm:inline max-w-[7rem] truncate">
                    {profile?.full_name?.split(' ')[0] || 'Account'}
                  </span>
                  <Icon name="ChevronDownIcon" size={13} />
                  {unread > 0 && (
                    <span
                      className="absolute -top-1 -right-1 bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                      aria-label={`${unread} unread messages`}
                    >
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </button>

                {accountOpen && (
                  <div className="absolute right-0 top-full mt-2 w-60 bg-card text-foreground rounded-xl shadow-2xl border border-border p-1.5 z-50">
                    {profile?.is_admin && (
                      <Link
                        href="/admin"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-amber-700 hover:bg-amber-50 transition-colors"
                      >
                        <Icon name="ShieldCheckIcon" size={17} />
                        Admin Console
                      </Link>
                    )}
                    <Link
                      href="/my-dashboard"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted transition-colors"
                    >
                      <Icon name="Squares2X2Icon" size={17} className="text-primary" />
                      My Dashboard
                    </Link>
                    <Link
                      href="/messaging-centre"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted transition-colors"
                    >
                      <span className="flex items-center gap-2.5">
                        <Icon name="ChatBubbleLeftRightIcon" size={17} className="text-primary" />
                        Messages
                      </span>
                      {unread > 0 && (
                        <span className="bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                    </Link>
                    <Link
                      href="/user-profile"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted transition-colors"
                    >
                      <Icon name="UserCircleIcon" size={17} className="text-primary" />
                      My Profile
                    </Link>
                    <Link
                      href="/membership-pricing"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted transition-colors 2xl:hidden"
                    >
                      <Icon name="StarIcon" size={17} className="text-amber-500" variant="solid" />
                      Membership
                    </Link>
                    <div className="border-t border-border my-1" />
                    <button
                      type="button"
                      onClick={() => { setAccountOpen(false); handleSignOut(); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted transition-colors text-left"
                    >
                      <Icon name="ArrowRightOnRectangleIcon" size={17} className="text-muted-foreground" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                href="/sign-up-login-screen"
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-3 py-2 rounded-md transition-all duration-150 whitespace-nowrap"
              >
                <Icon name="UserCircleIcon" size={16} />
                <span className="hidden sm:inline">Login</span>
              </Link>
            )}

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-primary border-t border-green-700 max-h-[75vh] overflow-y-auto">
          <nav className="max-w-screen-2xl mx-auto px-4 py-3 flex flex-col gap-1">
            {sectors.map((item) => (
              <Link
                key={`mob-nav-${item.label}`}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-green-100 hover:bg-white/10 hover:text-white transition-all"
              >
                <Icon name={item.icon} size={18} />
                {item.label}
              </Link>
            ))}
            <div className="border-t border-green-700 mt-2 pt-2 flex flex-col gap-1">
              <Link
                href="/membership-pricing"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-amber-300 hover:bg-white/10"
              >
                <Icon name="StarIcon" size={18} variant="solid" />
                Membership Plans
              </Link>
              <Link
                href="/post-listing"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold bg-accent text-white"
              >
                <Icon name="PlusCircleIcon" size={18} />
                Post a Listing
              </Link>
              {profile?.is_admin && (
                <Link
                  href="/admin"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-amber-300 hover:bg-white/10"
                >
                  <Icon name="ShieldCheckIcon" size={18} />
                  Admin Console
                </Link>
              )}
              {user ? (
                <>
                  <Link
                    href="/my-dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-green-100 hover:bg-white/10"
                  >
                    <Icon name="Squares2X2Icon" size={18} />
                    My Dashboard
                  </Link>
                  <Link
                    href="/messaging-centre"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-green-100 hover:bg-white/10"
                  >
                    <Icon name="ChatBubbleLeftRightIcon" size={18} />
                    Messaging Centre
                    {unread > 0 && (
                      <span className="bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )}
                  </Link>
                  <Link
                    href="/user-profile"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-green-100 hover:bg-white/10"
                  >
                    <Icon name="UserCircleIcon" size={18} />
                    My Profile
                  </Link>
                  <button
                    onClick={() => { handleSignOut(); setMobileOpen(false); }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-green-100 hover:bg-white/10 text-left"
                  >
                    <Icon name="ArrowRightOnRectangleIcon" size={18} />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  href="/sign-up-login-screen"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-green-100 hover:bg-white/10"
                >
                  <Icon name="ArrowRightOnRectangleIcon" size={18} />
                  Login / Register
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
